import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../auth/AuthProvider';
import { useSessionData } from '../../data/SessionData';
import { useSiteMetricFlags } from '../../../utils/siteMetricFlags';
import { buildNavigation } from '../../../util/routeNavigation';
import { toSlug } from '../../../util/strings';

function RouteSidebar({ onToggle, placement }) {
  const location = useLocation();
  const routeParams = useParams();
  const { plants } = useSessionData();
  const { hasPermission, user } = useAuth();
  const plantName = routeParams.plantName || routeParams.portfolioName;
  const metricFlags = useSiteMetricFlags(plantName);
  const [openMenus, setOpenMenus] = useState(() => new Set());

  const menuItems = useMemo(
    () => buildNavigation(placement, {
      plants,
      hasPermission,
      roleId: user?.role_id,
      metricFlags,
      params: {
        ...routeParams,
        plantName,
      },
    }),
    [hasPermission, metricFlags, placement, plantName, plants, routeParams, user?.role_id],
  );

  useEffect(() => {
    const pathname = location.pathname.toLowerCase();
    const activeParents = menuItems.filter((item) => (
      item.submenu && pathname.startsWith(item.path.toLowerCase())
    ));
    if (!activeParents.length) {
      return;
    }
    setOpenMenus((previous) => {
      const next = new Set(previous);
      activeParents.forEach((item) => next.add(item.label));
      return next.size === previous.size ? previous : next;
    });
  }, [location.pathname, menuItems]);

  const toggleMenu = (label) => {
    setOpenMenus((previous) => {
      const next = new Set(previous);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const pathname = location.pathname.toLowerCase();

  return (
    <div data-testid="sidebar" className="sidebar p-5 d-flex flex-column justify-content-between">
      <ul className="w-100 list-group list-group-flush pb-5 mb-5">
        {menuItems.map((item) => {
          const isActive = pathname.startsWith(item.path.toLowerCase());
          const isOpen = openMenus.has(item.label);

          return (
            <li className="list-group-item" key={`${item.label}-${item.path}`}>
              {item.submenu ? (
                <>
                  <button
                    type="button"
                    data-testid={`sidebar-section-${toSlug(item.label)}`}
                    className={`sidebar-menu-toggle ${isOpen ? 'open' : 'closed'} ${isActive ? 'active' : ''}`}
                    onClick={() => toggleMenu(item.label)}
                  >
                    <span>
                      {item.icon ? <FontAwesomeIcon icon={item.icon} /> : null}
                      {item.label}
                    </span>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </button>
                  <ul className={`list-group list-group-flush sidebar-submenu ${isOpen ? 'is-open' : ''}`}>
                    {item.submenu.map((child) => (
                      <li key={`${child.label}-${child.path}`} className="list-group-item">
                        <Link
                          to={child.path}
                          data-testid={`sidebar-link-${toSlug(child.label)}`}
                          className={pathname === child.path.toLowerCase() ? 'active' : undefined}
                        >
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </>
              ) : (
                <Link
                  to={item.path}
                  data-testid={`sidebar-link-${toSlug(item.label)}`}
                  className={isActive ? 'active' : undefined}
                >
                  <span>
                    {item.icon ? <FontAwesomeIcon icon={item.icon} /> : null}
                    {item.label}
                  </span>
                </Link>
              )}
            </li>
          );
        })}
      </ul>
      <div className="sidebarToggle" onClick={onToggle}>
        <div className="hide">&#9650; HIDE &#9650;</div>
        <div className="show">&#9660; SHOW &#9660;</div>
      </div>
    </div>
  );
}

export default RouteSidebar;
